import axiosInstance from './axiosInstance';

const CENTRAL_DB_BASE = import.meta.env.VITE_CENTRAL_DB_API_URL as string | undefined;

const buildUrl = (path = ''): string =>
  CENTRAL_DB_BASE
    ? `${CENTRAL_DB_BASE.replace(/\/$/, '')}/timesheets/defaults${path}`
    : `/timesheets/defaults${path}`;

export const HOUR_REPORTING_WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

export type HourReportingWeekdayKey = typeof HOUR_REPORTING_WEEKDAYS[number];

export type HourReportingWeekdayDefaults = {
  startTime: string | null;
  endTime: string | null;
  breakMinutes: number | null;
};

export type HourReportingDefaults = {
  weekdays: Partial<Record<HourReportingWeekdayKey, HourReportingWeekdayDefaults>>;
};

export type HourReportingDefaultsSummary = {
  configuredWeekdays: number;
  weekdaysWithStartTime: number;
  weekdaysWithEndTime: number;
  weekdaysWithBreakMinutes: number;
  hasStartTimeDefaults: boolean;
  hasEndTimeDefaults: boolean;
  hasBreakDefaults: boolean;
};

export type HourReportingPlacementOverview = {
  id: string;
  label: string;
  workerName: string | null;
  placementNumber: number | null;
  startDate: string | null;
  endDate: string | null;
};

export type HourReportingProjectOverview = {
  aanvraagNummer: number;
  editable: boolean;
  label: string;
  activePlacementCount: number;
  activePlacements: HourReportingPlacementOverview[];
  defaults: HourReportingDefaults | null;
  summary: HourReportingDefaultsSummary;
};

export type HourReportingCompanyOverview = {
  businessId: string | null;
  editable: boolean;
  label: string;
  easyflexRelatieRegistratienummer: number | null;
  activePlacementCount: number;
  activePlacements: HourReportingPlacementOverview[];
  defaults: HourReportingDefaults | null;
  summary: HourReportingDefaultsSummary;
  projects: HourReportingProjectOverview[];
};

export type HourReportingDefaultsOverviewResponse = {
  items: HourReportingCompanyOverview[];
};

export type HourReportingDefaultsPayload = Partial<
  Record<
    HourReportingWeekdayKey,
    {
      startTime?: string | null;
      endTime?: string | null;
      breakMinutes?: number | null;
    } | null
  >
>;

export async function listHourReportingDefaultsOverview(): Promise<HourReportingDefaultsOverviewResponse> {
  const response = await axiosInstance.get<HourReportingDefaultsOverviewResponse>(buildUrl('/overview'));
  return response.data;
}

export async function updateCompanyHourReportingDefaults(
  businessId: string,
  payload: HourReportingDefaultsPayload,
): Promise<void> {
  await axiosInstance.patch(buildUrl(`/businesses/${businessId}`), payload);
}

export async function updateProjectHourReportingDefaults(
  aanvraagNummer: number,
  payload: HourReportingDefaultsPayload,
): Promise<void> {
  await axiosInstance.patch(buildUrl(`/projects/${aanvraagNummer}`), payload);
}
